import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Check, Loader2, PartyPopper, ArrowRight } from 'lucide-react';
import confetti from 'canvas-confetti';

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await api.get(`/billing/checkout-status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(data);
        
        // Trigger confetti celebration
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPollCount(prev => prev + 1);
      
      if (pollCount >= maxAttempts - 1) {
        setStatus('error');
      } else {
        setTimeout(pollPaymentStatus, 2000);
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Processing Your Payment
          </h2>
          <p className="text-gray-600">
            Please wait while we confirm your subscription...
          </p>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full text-center border-red-200">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Payment Status Unknown
          </h2>
          <p className="text-gray-600 mb-6">
            We couldn't confirm your payment status. If you completed the payment, 
            it may take a few minutes to process. Please check your email for confirmation.
          </p>
          <div className="space-y-3">
            <Button 
              className="w-full"
              onClick={() => window.location.reload()}
            >
              Try Again
            </Button>
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50 flex items-center justify-center p-4">
      <Card className="p-8 max-w-md w-full text-center border-green-200" data-testid="payment-success-card">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-green-600" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Welcome to Integrity Home Care! 🎉
        </h1>

        <p className="text-gray-600 mb-6">
          Your subscription has been activated successfully. 
          You now have full access to all features.
        </p>

        {/* Payment Details */}
        {paymentDetails && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-medium text-gray-900 mb-2">Payment Details</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Amount:</span>
                <span className="font-medium text-gray-900">
                  ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Status:</span>
                <span className="font-medium text-green-600">Paid</span>
              </div>
            </div>
          </div>
        )}

        {/* What's Next */}
        <div className="bg-purple-50 rounded-lg p-4 mb-6 text-left">
          <h3 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
            <PartyPopper className="w-5 h-5" />
            What's Next?
          </h3>
          <ul className="text-sm text-purple-800 space-y-1">
            <li>• Set up your care team and clients</li>
            <li>• Configure your EMR preferences</li>
            <li>• Assign training courses to staff</li>
            <li>• Start scheduling shifts</li>
          </ul>
        </div>

        {/* CTA */}
        <Button 
          className="w-full bg-green-600 hover:bg-green-700"
          onClick={() => navigate('/dashboard')}
          data-testid="go-to-dashboard-btn"
        >
          Go to Dashboard
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Card>
    </div>
  );
}
